import { useEffect, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router'

import { PageLevelTableWrapper, useTableReducer } from '@base'
import { Table as TableV2, Pill as PillV2 } from '@baseV2'
import { logisticsSearchFilter, strings } from '@constants'
import { getAllLogisticsRequests } from '@data'
import { paths } from '@routes'
import { displayAmount, displayName } from '@utils'
import styled from 'styled-components'

const LinkHighlight = styled.div`
	color: ${({ theme }) => theme.colors.brand.primary};
	cursor: pointer;
`

// Map a logistics status to a v2 Pill colour.
const statusColor = (status) => {
	if (status === 'ACTIVE') return 'info'
	if (status === 'ORDER_PLACED') return 'warning'
	return 'neutral'
}

const AdminLogisticsList = ({ status }) => {
	const navigate = useNavigate()
	const [state, dispatch, actions] = useTableReducer(logisticsSearchFilter)

	const { pathname } = useLocation()

	const enquiryClickHandler = (e, enquiryId) => {
		e.stopPropagation()
		navigate(paths.admin_manage_enquiry_manage(enquiryId))
	}

	const columns = useMemo(() => {
		let cols = [
			{
				label: `${strings('enquiry_id')} / ${strings('order_id')}`,
				key: (data) => data,
				renderer: (data) => (
					<>
						<div>
							<LinkHighlight onClick={(e) => enquiryClickHandler(e, data.enquiry_id)}>
								<b>{data?.enquiry_id ?? '--'}</b>
							</LinkHighlight>
						</div>
						<div>
							<small>{data?.order_id ?? '--'}</small>
						</div>
					</>
				),
			},
			{ label: strings('logistics_partner'), key: 'logistics_partner', renderer: (data) => displayName(data) },
			{
				label: `${strings('supplier')} / ${strings('address')}`,
				key: (data) => data,
				renderer: (data) => (
					<>
						<div>
							<b>{displayName(data?.supplier) ?? '--'}</b>
						</div>
						<div>
							<small>{`${
								data?.dispatch_address?.city + (data?.dispatch_address?.pincode ? ', ' + data?.dispatch_address?.pincode : '')
							}`}</small>
						</div>
					</>
				),
			},
			{
				label: strings('quotation'),
				key: (data) => data,
				renderer: (data) =>
					data?.lumpsum_logistics_quote > 0
						? `${displayAmount(data?.lumpsum_logistics_quote)} lumpsum`
						: `${displayAmount(data?.logistics_quote_per_mt)} per MT`,
			},
			{ label: strings('logistics_manager'), key: 'logistics_managers', renderer: (data) => displayName(data[0]) },
			{
				label: strings('total', 'quantity'),
				key: 'vehicle_capacity_list',
				renderer: (data) => {
					const totalQuantity = (data ?? []).reduce((sum, item) => sum + (Number(item?.capacity) || 0) * (Number(item?.count) || 0), 0)
					return `${totalQuantity} MT`
				},
			},
		]
		if (pathname?.includes('all')) {
			cols = [
				...cols,
				{
					label: strings('status'),
					key: 'logistics_status',
					renderer: (data) => (
						<PillV2 color={statusColor(data)} variant='subtle'>
							{data ?? '--'}
						</PillV2>
					),
					mobile: true,
					sortable: false,
				},
			]
		}

		// v2 Table enables sorting by default; keep these columns non-sortable like before.
		return cols.map((col) => ({ sortable: false, ...col }))
	}, [pathname])

	useEffect(() => {
		if (state.loading) return
		dispatch(actions.fetchTableDataProgress())
		getAllLogisticsRequests(state.query.page, 10, status, state.query.filters)
			.then((response) => {
				dispatch(actions.fetchTableDataSuccess(response.data, response.count))
			})
			.catch((err) => {
				dispatch(actions.fetchTableDataFailure(err))
			})
	}, [state.query, status])

	const changePageHandler = (next = true) => {
		dispatch(actions.changePage(next))
	}
	const rowClickHandler = (row) => {
		if (status === 'ORDER_PLACED') {
			navigate(`${paths.admin_manage_enquiry_order_edit(row?.order_id)}?logisticsid=${row?.logistics_id}`)
		} else {
			navigate(`${paths.admin_manage_enquiry_manage(row?.enquiry_id)}?logisticsid=${row?.logistics_id}`)
		}
	}
	return (
		<PageLevelTableWrapper>
			<TableV2
				columns={columns}
				data={state.data}
				loading={state.loading}
				rowsToDisplay={10}
				totalItemCount={state.total_count}
				page={state.query.page}
				onPageChange={changePageHandler}
				onRowClick={rowClickHandler}
				emptyState={{ text: 'No logistics found' }}
			/>
		</PageLevelTableWrapper>
	)
}

export default AdminLogisticsList
